import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import CollectionService from "@/services/CollectionService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  TrashIcon,
  ReloadIcon,
  Pencil1Icon,
  VideoIcon,
} from "@radix-ui/react-icons";

import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import useStore from "@/store";
import posthog from "posthog-js";
import { DEFAULT_LEARNING_LANGUAGE, toLearningLanguage } from "@/constants/langcraftLanguages";
import LanguagePicker from "@/components/LanguagePicker";

const createEmptyContent = () => ({ id: null, text: "" });

const CollectionEditor = ({ collection }) => {
  const navigate = useNavigate();

  const { user, setCurrentCollectionId } = useStore().user;
  const { setCurrentCollectionById } = useStore().collections;

  const [language, setLanguage] = useState(
    toLearningLanguage(collection?.language) || DEFAULT_LEARNING_LANGUAGE
  );

  const { collectionId } = useParams();

  const [title, setTitle] = useState(collection?.title || "Untitled");

  const [youtubeUrl, setYoutubeUrl] = useState("");

  const scrollContainerRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);

  // const [contents, setContents] = useState(
  //   collection ? collection.map((item) => item.text) : [""]
  // );
  const [contents, setContents] = useState(() => {
    if (collection && collection.contents && collection.contents.length > 0) {
      return collection.contents.map((item) => ({
        id: item.id,
        text: item.text,
      }));
    }

    return [createEmptyContent()];
  });

  const [originalContents, setOriginalContents] = useState([]);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef(null);

  const handlePractice = async () => {
    try {
      await setCurrentCollectionId(collection.id);
      navigate("/");
    } catch (error) {
      console.error("Failed to set current collection ID:", error);
    }
  };

  useEffect(() => {
    // Automatically focus the title input when starting to edit
    if (isEditingTitle) {
      titleInputRef.current?.focus();
    }
  }, [isEditingTitle]);

  const onSave = async (collectionDetails) => {
    // Persist the collection updates or newly created entries

    if (collectionId) {
      let collection = await CollectionService.updateCollection(
        collectionId,
        collectionDetails.language,
        collectionDetails.title,
        originalContents,
        collectionDetails.contents,
        collectionDetails.type,
        collectionDetails.youtubeUrl
      );
      setOriginalContents(collectionDetails.contents);
      toast("Collection updated", {
        description: "The collection has been successfully updated!",
        action: {
          label: "Close",
          onClick: () => console.log("Undo action clicked"),
        },
      });
      posthog.capture("collection_updated");
    } else {
      // Logic for creating a new collection
      try {
        const newCollection =
          await CollectionService.createCollectionWithContents(
            collectionDetails.title,
            collectionDetails.language,
            collectionDetails.contents,
            collectionDetails.type,
            collectionDetails.youtubeUrl
          );

        // Assuming `newCollection` contains the ID of the created collection
        // And `setCurrentCollectionId` is a method in your user store that updates the current collection ID
        if (newCollection && newCollection.id) {
          setCurrentCollectionId(newCollection.id);
          setCurrentCollectionById(newCollection.id);
        }

        toast("Success", {
          description: "The collection was created successfully!",
          action: {
            label: "Close",
            onClick: () => console.log("Close"),
          },
        });

        posthog.capture("collection_created");
        navigate("/");
      } catch (error) {
        console.error("Failed to create collection:", error);
        toast("Error", {
          description: `Failed to create collection: ${error.message || "An error occurred"}`,
          action: {
            label: "Close",
            onClick: () => console.log("Error Close"),
          },
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (!collection) {
      return;
    }

    setTitle(collection.title);
    setLanguage(toLearningLanguage(collection.language) || DEFAULT_LEARNING_LANGUAGE);
    setYoutubeUrl(collection.video_url || "");

    const normalizedOriginalContents = collection.contents
      ? collection.contents.map((item) => ({
          id: item.id,
          text: item.text,
        }))
      : [];

    const nextContents =
      normalizedOriginalContents.length > 0
        ? normalizedOriginalContents
        : [createEmptyContent()];

    setOriginalContents(normalizedOriginalContents);
    setContents(nextContents);
  }, [collection]);

  // Handle clicking outside the title input to stop editing
  const handleClickOutside = (event) => {
    if (
      titleInputRef.current &&
      !titleInputRef.current.contains(event.target)
    ) {
      setIsEditingTitle(false);
    }
  };

  useEffect(() => {
    // Attach the event listener to handle clicks outside the title input
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Remove the event listener when the component is unmounted
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle change in textareas
  const handleChange = (index, value) => {
    const newContents = [...contents];
    newContents[index] = { ...newContents[index], text: value };
    setContents(newContents);
  };

  // Insert a new textarea with empty content at a specified position
  const handleAddAbove = (index) => {
    const newContents = [
      ...contents.slice(0, index),
      createEmptyContent(),
      ...contents.slice(index),
    ];
    setContents(newContents);
  };

  const handleAddBelow = (index) => {
    const newContents = [
      ...contents.slice(0, index + 1),
      createEmptyContent(), // Add a new content item below the current index
      ...contents.slice(index + 1),
    ];
    setContents(newContents);

    // Check if the addition is happening at the last item
    if (index === contents.length - 1) {
      // Wait for the state to update and the component to re-render
      setTimeout(scrollToBottom, 0);
    }
  };

  // Delete a specific textarea/content
  const handleDelete = (index) => {
    const newContents = contents.filter((_, i) => i !== index);
    setContents(newContents);
  };

  const handleAddInitialCard = () => {
    setContents([createEmptyContent()]);
    setTimeout(scrollToBottom, 0);
  };

  // Save the collection with its title
  const handleSave = async () => {
    setIsSaving(true); // Indicate that saving has started
    try {
      const collectionDetails = {
        title,
        language,
        youtubeUrl,
        type: youtubeUrl.trim() !== "" ? "youtube" : "text",
        contents: contents
          .filter((content) => content.text.trim() !== "")
          .map((content) => ({ id: content.id, text: content.text })),
      };

      await onSave(collectionDetails);

      // If saving is successful and you navigate away, consider where and how to setIsSaving(false)
      // navigate("/collections");
    } catch (error) {
      console.error("Failed to save collection:", error);
      // Optionally, show an error message to the user
    } finally {
      setIsSaving(false); // Indicate that saving has finished
    }
  };

  const scrollToBottom = () => {
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const height = scrollContainerRef.current.clientHeight;
    const maxScrollTop = scrollHeight - height;
    scrollContainerRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
  };

  return (
    <div
      className="bg-grid relative isolate min-h-screen overflow-hidden text-fg"
    >

      <div className="relative mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 pb-16 pt-24 sm:px-8 md:px-12">
        <header className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface/80 text-fg transition hover:border-primary/40 hover:bg-elevated focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
                aria-label="Back to collections"
                onClick={() => navigate("/collections")}
              >
                <span className="text-lg font-semibold leading-none text-fg">
                  ←
                </span>
              </Button>

              <div className="w-full sm:w-auto sm:min-w-[22rem]">
                <LanguagePicker
                  value={language}
                  onChange={setLanguage}
                  layout="row"
                  triggerClassName="h-10 rounded-full px-4 font-medium"
                />
              </div>

              {collection && collection.content_type === "youtube" && (
                <a
                  href={collection.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-elevated text-fg transition hover:border-primary/40 hover:bg-elevated"
                >
                  <VideoIcon className="h-4 w-4" />
                </a>
              )}
            </div>

            <div className="space-y-3">
              <div
                className="flex flex-col gap-2"
                onClick={() => setIsEditingTitle(true)}
              >
                {isEditingTitle ? (
                  <Input
                    ref={titleInputRef}
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    className="w-full border-0 bg-transparent px-0 text-3xl font-semibold tracking-tight text-fg focus-visible:ring-0"
                    placeholder="Collection Title"
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-semibold tracking-tight text-fg">
                      {title}
                    </h1>
                    <Pencil1Icon
                      className="h-4 w-4 cursor-pointer text-fg-muted transition hover:text-fg"
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsEditingTitle(true);
                      }}
                    />
                  </div>
                )}
              </div>
              <p className="max-w-xl text-sm text-fg-muted">
                Shape the passages and prompts that make this collection uniquely
                yours. Adjust, reorder, or add fresh material whenever inspiration
                strikes.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="rounded-full border border-line bg-elevated px-5 py-2 text-sm font-semibold text-fg transition hover:border-primary/40 hover:bg-elevated"
              onClick={handlePractice}
            >
              Practice
            </Button>
            <Button
              className="relative overflow-hidden rounded-full border border-primary/50 bg-primary/80 px-6 py-2 text-sm font-semibold text-primary-fg shadow-panel transition hover:bg-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <ReloadIcon className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </header>

        <section className="mt-12 flex-1">
          <div className="flex flex-col gap-8 rounded-3xl border border-line bg-surface/80 p-6 shadow-panel backdrop-blur-xl sm:p-8">
            <div className="flex items-center">
              <div className="text-xs uppercase tracking-[0.4em] text-fg-muted">
                Card Listings
              </div>
            </div>

            <div
              className="flex flex-col gap-6 overflow-y-auto pr-2"
              style={{ maxHeight: "calc(100vh - 360px)" }}
              ref={scrollContainerRef}
            >
              {contents.length > 0 ? (
                contents.map((content, index) => (
                  <div
                    key={`${content.id ?? "new"}-${index}`}
                    className="group relative rounded-3xl border border-line bg-surface p-5 shadow-panel transition hover:border-primary/40 hover:bg-elevated"
                  >
                    <TextareaAutosize
                      className="w-full resize-none border-none bg-transparent text-base leading-relaxed text-fg placeholder:text-fg-muted focus:outline-none"
                      value={content.text}
                      minRows={3}
                      placeholder="enter your word or sentence"
                      onChange={(event) =>
                        handleChange(index, event.target.value)
                      }
                    />

                    <div className="pointer-events-none absolute inset-0 rounded-3xl border border-line opacity-0 transition group-hover:opacity-100" />

                    <div className="pointer-events-auto absolute -top-4 left-1/2 -translate-x-1/2 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleAddAbove(index)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-elevated text-fg shadow-sm transition hover:border-primary/40 hover:bg-elevated"
                      >
                        +
                      </button>
                    </div>

                    <div className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-danger/40 bg-danger/20 text-danger shadow-sm transition hover:border-danger/60 hover:bg-danger/30"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="pointer-events-auto absolute -bottom-4 left-1/2 -translate-x-1/2 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleAddBelow(index)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-elevated text-fg shadow-sm transition hover:border-primary/40 hover:bg-elevated"
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
                  <button
                    type="button"
                    onClick={handleAddInitialCard}
                    className="flex h-14 w-14 items-center justify-center rounded-full border border-line bg-elevated text-3xl font-semibold text-fg shadow-panel transition hover:border-primary/40 hover:bg-elevated"
                  >
                    +
                  </button>
                  <p className="mt-4 text-sm font-medium text-fg-muted">
                    Add your first card
                  </p>
                  <p className="mt-1 text-xs text-fg-muted">
                    Start by adding a word or sentence to practice.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CollectionEditor;
